import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, CheckCircle, AlertTriangle, Loader, BarChart3, TrendingUp, 
  Bot, User, Sparkles, Plus, X, Calculator, Lightbulb, Save, HelpCircle, 
  Settings, ArrowRight, ArrowLeft, PlayCircle, Check, AlertCircle,
  Brain, Target, PieChart, Activity, Zap, Calendar, Building, Database
} from 'lucide-react';

const OrangeRedKPIInterface = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: "Hello! I'm your business intelligence assistant. I can analyze your KPIs, help you understand performance metrics, and learn new definitions when needed. What would you like to know about your business?",
      timestamp: new Date()
    }
  ]);
  
  const [currentInput, setCurrentInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSemanticBuilder, setShowSemanticBuilder] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [semanticBuilderData, setSemanticBuilderData] = useState(null);
  const [waitingForEntitySelection, setWaitingForEntitySelection] = useState(null);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [keyNavigationUsed, setKeyNavigationUsed] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Demo data with more comprehensive options
  const demoAvailableOptions = {
    periods: [
      'Q1', 'Q2', 'Q3', 'Q4', 'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December', 'YTD', 'MTD'
    ],
    fnyear: ['All', 'FY 2024', 'FY 2023', 'FY 2022', 'FY 2021', 'FY 2020'],
    distributors: [
      'All',  'Toyota Abu Dhabi Motors', 'Lexus Central', 'Lexus GCC Motors',
      'Redline Arabia Motors', 'Nova Toyota', 'Crystal Line Motors'
    ],
    semantics: ['income', 'revenue growth', 'efficiency', 'customer satisfaction', 'profitability'],
    kpis: [
      'Sales Volume', 'Revenue', 'Profit Margin', 'Customer Count', 'Operating Costs', 
      'Customer Satisfaction Score', 'Market Share', 'Employee Productivity', 'Quality Index',
      'Cost Per Acquisition', 'Customer Lifetime Value', 'Conversion Rate', 'Churn Rate'
    ]
  };

  // All available terms for autocomplete
  const allAvailableTerms = [
    ...demoAvailableOptions.periods.map(p => ({ type: 'period', value: p, color: 'bg-blue-100 text-blue-800' })),
    ...demoAvailableOptions.fnyear.map(f => ({ type: 'fnyear', value: f, color: 'bg-green-100 text-green-800' })),
    ...demoAvailableOptions.distributors.map(d => ({ type: 'distributor', value: d, color: 'bg-purple-100 text-purple-800' })),
    ...demoAvailableOptions.kpis.map(k => ({ type: 'kpi', value: k, color: 'bg-indigo-100 text-indigo-800' })),
    ...demoAvailableOptions.semantics.map(s => ({ type: 'semantic', value: s, color: 'bg-violet-100 text-violet-800' }))
  ];

  // Smart context-aware autocomplete logic
  const getSmartAutocompleteOptions = (input) => {
    // When waiting for entity, ALWAYS show options (even with empty input)
    if (waitingForEntitySelection) {
      const expectedType = waitingForEntitySelection.currentEntity;
      const entityOptions = demoAvailableOptions[expectedType === 'fnyear' ? 'fnyear' : expectedType + 's'] || demoAvailableOptions[expectedType] || [];
      
      const searchTerm = (input || '').toLowerCase().trim();
      
      if (searchTerm.length === 0) {
        // Show "All" + top 4-5 options when no input
        const defaultOptions = ['All'];
        const topOptions = entityOptions
          .filter(opt => opt.toLowerCase() !== 'all')
          .slice(0, 4);
        
        return [...defaultOptions, ...topOptions].map(option => ({ 
          type: expectedType, 
          value: option, 
          color: getEntityColor(expectedType),
          isEntityResponse: true
        }));
      } else {
        // Always include "All" + filtered options when typing
        const allOption = [{ 
          type: expectedType, 
          value: 'All', 
          color: getEntityColor(expectedType),
          isEntityResponse: true
        }];
        
        // Filter other options based on input
        const filteredOptions = entityOptions
          .filter(option => option.toLowerCase() !== 'all')
          .filter(option => 
            option.toLowerCase().includes(searchTerm) ||
            option.toLowerCase().startsWith(searchTerm)
          )
          .slice(0, 5)
          .map(option => ({ 
            type: expectedType, 
            value: option, 
            color: getEntityColor(expectedType),
            isEntityResponse: true
          }))
          .sort((a, b) => {
            // Prioritize exact matches and startsWith matches
            const aStarts = a.value.toLowerCase().startsWith(searchTerm);
            const bStarts = b.value.toLowerCase().startsWith(searchTerm);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return a.value.length - b.value.length;
          });

        return [...allOption, ...filteredOptions];
      }
    }
    
    // Normal autocomplete when not waiting for entities (only when user types something)
    if (!input || input.length < 1) return [];
    
    const searchTerm = input.toLowerCase().trim();
    const lastWord = searchTerm.split(' ').pop();
    const matches = allAvailableTerms
      .filter(term => 
        term.value.toLowerCase().includes(lastWord) ||
        term.value.toLowerCase().startsWith(lastWord)
      )
      .slice(0, 3)
      .sort((a, b) => {
        const aStarts = a.value.toLowerCase().startsWith(lastWord);
        const bStarts = b.value.toLowerCase().startsWith(lastWord);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.value.length - b.value.length;
      });

    return matches;
  };

  // Helper function to get entity colors - attention-seeking but lite
  const getEntityColor = (entityType) => {
    const colorMap = {
      'period': 'bg-blue-200 text-blue-700 border-blue-300',
      'fnyear': 'bg-emerald-200 text-emerald-700 border-emerald-300', 
      'distributor': 'bg-purple-200 text-purple-700 border-purple-300',
      'kpi': 'bg-indigo-200 text-indigo-700 border-indigo-300',
      'semantic': 'bg-violet-200 text-violet-700 border-violet-300'
    };
    return colorMap[entityType] || 'bg-gray-200 text-gray-700 border-gray-300';
  };

  // Handle smart autocomplete selection
  const handleSmartAutocompleteSelect = (term) => {
    // If this is an entity response, handle it as entity selection
    if (term.isEntityResponse && waitingForEntitySelection) {
      handleEntitySelection(waitingForEntitySelection.currentEntity, term.value);
      setCurrentInput('');
      setAutocompleteOptions([]);
      setSelectedSuggestionIndex(-1);
      setKeyNavigationUsed(false); // Reset for next entity
      return;
    }
    
    // Normal autocomplete for building queries
    const words = currentInput.split(' ');
    words[words.length - 1] = term.value;
    const newInput = words.join(' ');
    setCurrentInput(newInput);
    setAutocompleteOptions([]);
    setSelectedSuggestionIndex(-1);
    setKeyNavigationUsed(false);
    inputRef.current?.focus();
  };

  // Handle input changes with smart context-aware autocomplete
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentInput(value);
    
    const options = getSmartAutocompleteOptions(value);
    setAutocompleteOptions(options);
    setSelectedSuggestionIndex(-1); // Reset selection when options change
  };

  // Trigger autocomplete when entering entity selection mode
  useEffect(() => {
    if (waitingForEntitySelection) {
      // Force show options immediately when entity selection starts
      const options = getSmartAutocompleteOptions(''); // Pass empty string to trigger default options
      setAutocompleteOptions(options);
      setSelectedSuggestionIndex(-1);
      setKeyNavigationUsed(false); // Reset blinking state for new entity
      
      // Auto-focus the input when entity selection starts
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Ensure options are visible after focus
          const freshOptions = getSmartAutocompleteOptions(currentInput);
          setAutocompleteOptions(freshOptions);
        }
      }, 100);
    } else {
      setAutocompleteOptions([]);
      setSelectedSuggestionIndex(-1);
      setKeyNavigationUsed(false);
    }
  }, [waitingForEntitySelection]);

  // Also update options when currentInput changes AND we're waiting for entity
  useEffect(() => {
    if (waitingForEntitySelection) {
      const options = getSmartAutocompleteOptions(currentInput);
      setAutocompleteOptions(options);
      setSelectedSuggestionIndex(-1);
      // Don't reset keyNavigationUsed here - keep the non-blinking state during typing
    }
  }, [currentInput, waitingForEntitySelection]);

  // Enhanced keyboard navigation - simplified and robust
  const handleKeyDown = (e) => {
    console.log('Key pressed:', e.key, 'Waiting for entity:', !!waitingForEntitySelection, 'Options:', autocompleteOptions.length);
    
    // Always handle arrow keys when waiting for entity selection OR when options are visible
    if (waitingForEntitySelection || autocompleteOptions.length > 0) {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          console.log('Arrow right pressed');
          setKeyNavigationUsed(true); // Stop blinking once user starts navigating
          if (autocompleteOptions.length > 0) {
            setSelectedSuggestionIndex(prev => {
              const newIndex = prev < autocompleteOptions.length - 1 ? prev + 1 : 0;
              console.log('Moving right to index:', newIndex);
              return newIndex;
            });
          }
          return;
        case 'ArrowLeft':
          e.preventDefault();
          console.log('Arrow left pressed');
          setKeyNavigationUsed(true); // Stop blinking once user starts navigating
          if (autocompleteOptions.length > 0) {
            setSelectedSuggestionIndex(prev => {
              const newIndex = prev > 0 ? prev - 1 : autocompleteOptions.length - 1;
              console.log('Moving left to index:', newIndex);
              return newIndex;
            });
          }
          return;
        case 'Tab':
          e.preventDefault();
          setKeyNavigationUsed(true);
          if (selectedSuggestionIndex >= 0 && autocompleteOptions.length > 0) {
            handleSmartAutocompleteSelect(autocompleteOptions[selectedSuggestionIndex]);
          } else if (autocompleteOptions.length > 0) {
            handleSmartAutocompleteSelect(autocompleteOptions[0]);
          }
          return;
        case 'Escape':
          if (waitingForEntitySelection) {
            // Don't close options when waiting for entity, just reset selection
            setSelectedSuggestionIndex(-1);
          } else {
            setAutocompleteOptions([]);
            setSelectedSuggestionIndex(-1);
            setKeyNavigationUsed(false);
          }
          return;
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there's a selected suggestion, use it
      if (selectedSuggestionIndex >= 0 && autocompleteOptions.length > 0) {
        handleSmartAutocompleteSelect(autocompleteOptions[selectedSuggestionIndex]);
        return;
      }
      
      // If waiting for entity selection and user typed something
      if (waitingForEntitySelection && currentInput.trim()) {
        const inputValue = currentInput.trim();
        const expectedType = waitingForEntitySelection.currentEntity;
        const entityOptions = demoAvailableOptions[expectedType === 'fnyear' ? 'fnyear' : expectedType + 's'] || demoAvailableOptions[expectedType] || [];
        
        // Check if input matches any option (case insensitive)
        const matchedOption = entityOptions.find(option => 
          option.toLowerCase() === inputValue.toLowerCase() ||
          option.toLowerCase().includes(inputValue.toLowerCase())
        );
        
        if (matchedOption) {
          handleEntitySelection(expectedType, matchedOption);
          setCurrentInput('');
          setAutocompleteOptions([]);
          setSelectedSuggestionIndex(-1);
          setKeyNavigationUsed(false);
          return;
        }
        
        // If no exact match but has autocomplete options, use first one
        if (autocompleteOptions.length > 0 && autocompleteOptions[0].isEntityResponse) {
          handleSmartAutocompleteSelect(autocompleteOptions[0]);
          return;
        }
        
        // If no match found, still allow as custom input
        handleEntitySelection(expectedType, inputValue);
        setCurrentInput('');
        setAutocompleteOptions([]);
        setSelectedSuggestionIndex(-1);
        setKeyNavigationUsed(false);
        return;
      }
      
      // Normal message sending
      if (!waitingForEntitySelection) {
        handleSendMessage();
      }
      return;
    }
  };

  const fieldConfig = {
    'period': { 
      icon: Calendar, 
      label: 'Time Period',
      color: 'violet',
      prompt: "Which time period would you like to analyze?"
    },
    'fnyear': { 
      icon: Calendar, 
      label: 'Financial Year',
      color: 'indigo',
      prompt: "For which financial year?"
    },
    'distributor': { 
      icon: Building, 
      label: 'Distributor',
      color: 'purple',
      prompt: "Which distributor should I analyze?"
    },
    'semantic': { 
      icon: TrendingUp, 
      label: 'Metric Type',
      color: 'blue',
      prompt: "What type of metric are you looking for?"
    },
    'kpi': { 
      icon: BarChart3, 
      label: 'KPI',
      color: 'cyan',
      prompt: "Which specific KPI interests you?"
    }
  };

  // Premium Circular Progress Component with better achievement display
  const CircularProgress = ({ value, target, size = 120, label }) => {
    const percentage = target > 0 ? (value / target) * 100 : 0;
    const displayPercentage = Math.min(percentage, 100); // Cap display at 100% for visual
    const isOverAchieving = percentage > 100;
    const isUnderperforming = percentage < 70; // Red for under 70%
    
    const radius = (size - 12) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = `${(displayPercentage / 100) * circumference} ${circumference}`;
    
    // Color logic
    let strokeColor = "#8b5cf6"; // Default purple
    let textColor = "text-violet-600";
    
    if (isOverAchieving) {
      strokeColor = "#10b981";
      textColor = "text-emerald-600";
    } else if (isUnderperforming) {
      strokeColor = "#ef4444";
      textColor = "text-red-600";
    }
    
    return (
      <div className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <svg height={size} width={size} className="transform -rotate-90">
            <circle
              stroke="#f3f4f6"
              fill="transparent"
              strokeWidth="6"
              r={radius}
              cx={size / 2}
              cy={size / 2}
            />
            <circle
              stroke={strokeColor}
              fill="transparent"
              strokeWidth="6"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={0}
              strokeLinecap="round"
              r={radius}
              cx={size / 2}
              cy={size / 2}
              className="transition-all duration-700 ease-in-out"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={`text-lg font-semibold ${textColor}`}>
                {percentage.toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {isOverAchieving ? 'Exceeded' : isUnderperforming ? 'Below Target' : 'On Track'}
              </div>
            </div>
          </div>
        </div>
        {label && <div className="mt-3 text-sm text-gray-700 text-center font-medium max-w-24 leading-tight">{label}</div>}
      </div>
    );
  };

  // Premium Bar Chart Component with Violet/Blue theme
  const SimpleBarChart = ({ data, title }) => {
    if (!data || data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => d.value));
    
    // Premium color palette for data visualization
    const premiumColors = [
      'bg-gradient-to-r from-violet-500 to-purple-600',
      'bg-gradient-to-r from-blue-500 to-indigo-600', 
      'bg-gradient-to-r from-purple-500 to-violet-600',
      'bg-gradient-to-r from-indigo-500 to-blue-600',
      'bg-gradient-to-r from-cyan-500 to-teal-600'
    ];
    
    return (
      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">{title}</h3>
        <div className="space-y-4">
          {data.map((item, index) => (
            <div key={index} className="flex items-center">
              <div className="w-28 text-sm text-gray-700 text-right mr-4 truncate font-medium">
                {item.label}
              </div>
              <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                <div 
                  className={`h-8 rounded-full transition-all duration-700 ease-out flex items-center justify-end pr-3 ${
                    premiumColors[index % premiumColors.length]
                  }`}
                  style={{ 
                    width: `${Math.max((item.value / maxValue) * 100, 8)}%`,
                    filter: 'drop-shadow(0 2px 4px rgba(139, 92, 246, 0.2))'
                  }}
                >
                  <span className="text-white text-sm font-semibold">
                    {item.formatted || item.value.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Smart Entity Selection Card - no search box, just filtered options
  const SmartEntitySelectionCard = ({ field, options, onSelect, onSkip, collectedEntities }) => {
    const [filteredOptions, setFilteredOptions] = useState([]);
    
    const config = fieldConfig[field];
    const Icon = config?.icon || BarChart3;
    
    useEffect(() => {
      // Filter options based on current input
      const searchTerm = currentInput.toLowerCase().trim();
      
      if (searchTerm.length === 0) {
        // Show default options when no input
        const defaultOptions = ['All'];
        const topOptions = options
          .filter(opt => opt.toLowerCase() !== 'all')
          .slice(0, 5); // Show top 5 options by default
        setFilteredOptions([...defaultOptions, ...topOptions]);
      } else {
        // Show "All" plus filtered results
        const defaultOptions = ['All'];
        const filtered = options
          .filter(opt => opt.toLowerCase() !== 'all')
          .filter(opt => 
            opt.toLowerCase().includes(searchTerm) ||
            opt.toLowerCase().startsWith(searchTerm)
          )
          .slice(0, 8); // Show more when user is typing
        
        setFilteredOptions([...defaultOptions, ...filtered]);
      }
    }, [currentInput, options]);
    
    const getColorClasses = (color) => {
      const colorMap = {
        violet: { bg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200', hover: 'hover:bg-violet-200' },
        indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:bg-indigo-200' },
        purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', hover: 'hover:bg-purple-200' },
        blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:bg-blue-200' },
        cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', hover: 'hover:bg-cyan-200' }
      };
      return colorMap[color] || colorMap.violet;
    };
    
    const colors = getColorClasses(config?.color);
    
    return (
      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
        {/* Show collected entities if any */}
        {collectedEntities && Object.keys(collectedEntities).length > 0 && (
          <div className="mb-3 p-2 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
            <p className="text-xs font-medium text-orange-800 mb-1">Already selected:</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(collectedEntities).map(([key, value]) => (
                <span key={key} className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">
                  {fieldConfig[key]?.label || key}: {value}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${colors.bg}`}>
            <Icon className={`w-3 h-3 ${colors.text}`} />
          </div>
          <p className="text-sm text-gray-800 font-medium">{config?.prompt}</p>
        </div>


        
        {/* Quick Select Options - dynamically filtered */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
          {filteredOptions.map((option) => (
            <button
              key={option}
              onClick={() => onSelect(field, option)}
              className={`px-2 py-1.5 text-xs rounded-md border transition-all hover:scale-105 ${
                option === 'All' 
                  ? 'border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 font-medium' 
                  : `${colors.border} ${colors.bg} ${colors.text} ${colors.hover}`
              }`}
            >
              {option}
            </button>
          ))}
        </div>
        
        {/* Show filtering info */}
        {currentInput && (
          <div className="mb-2 text-xs text-gray-500">
            {filteredOptions.length === 1 ? (
              <span className="text-red-600">No matches found for "{currentInput}"</span>
            ) : (
              <span>
                {filteredOptions.length - 1} options match "{currentInput}" • Press Enter to use your input
              </span>
            )}
          </div>
        )}
        
        <button
          onClick={() => onSkip(field)}
          className="text-xs text-gray-600 hover:text-gray-800 underline"
        >
          Skip this for now
        </button>
      </div>
    );
  };

  // Simplified Semantic Definition Builder with Orange/Red theme
  const SemanticDefinitionBuilder = ({ onClose, onSave, unknownTerm }) => {
    const [step, setStep] = useState(1);
    const [definitionType, setDefinitionType] = useState(null);
    const [termName, setTermName] = useState(unknownTerm || '');
    const [description, setDescription] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [equation, setEquation] = useState('');
    const [unit, setUnit] = useState('number');
    const [searchTerm, setSearchTerm] = useState('');

    const examples = {
      kpi: {
        title: "Use Existing KPIs",
        example: "Performance = (Revenue + Customer_Satisfaction - Operating_Costs) / 3",
        items: ["Revenue", "Customer_Satisfaction", "Operating_Costs"]
      },
      semantic: {
        title: "Use Semantic Metrics", 
        example: "Performance = Income * 0.7 + Efficiency * 0.3",
        items: ["Income", "Efficiency"]
      }
    };

    const filteredItems = (definitionType === 'kpi' ? demoAvailableOptions.kpis : demoAvailableOptions.semantics)
      .filter(item => item.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleItemToggle = (item) => {
      setSelectedItems(prev => 
        prev.includes(item) 
          ? prev.filter(i => i !== item)
          : [...prev, item]
      );
    };

    const generateEquationSuggestion = () => {
      if (selectedItems.length === 0) return '';
      if (selectedItems.length === 1) return selectedItems[0].replace(/ /g, '_');
      
      const variables = selectedItems.map(item => item.replace(/ /g, '_')).join(' + ');
      return `(${variables}) / ${selectedItems.length}`;
    };

    const handleSave = () => {
      const newDefinition = {
        term: termName,
        description,
        type: definitionType,
        formula: equation,
        unit,
        categories: selectedItems
      };
      onSave(newDefinition);
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header with Orange/Red theme */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Define "{termName}"</h2>
                <p className="text-sm text-gray-600">Help me understand this business term</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-400 p-4 rounded">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-orange-900">Let's define "{termName}"</h3>
                      <p className="text-orange-800 text-sm mt-1">
                        I don't have a definition for this term. Can you help me understand what it means in your business?
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Term Name
                  </label>
                  <input
                    type="text"
                    value={termName}
                    onChange={(e) => setTermName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g., performance, efficiency"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Brief description of what this means..."
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!termName.trim()}
                    className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 disabled:opacity-50 transition-all"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Type Selection */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    How should I calculate "{termName}"?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div
                    className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                      definitionType === 'kpi' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                    onClick={() => setDefinitionType('kpi')}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <BarChart3 className="w-6 h-6 text-orange-600" />
                      <h4 className="font-semibold text-gray-900">{examples.kpi.title}</h4>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <p className="text-sm text-gray-600 mb-2">Example:</p>
                      <div className="text-xs font-mono bg-gray-50 p-2 rounded">{examples.kpi.example}</div>
                    </div>
                  </div>

                  <div
                    className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                      definitionType === 'semantic' 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-gray-200 hover:border-red-300'
                    }`}
                    onClick={() => setDefinitionType('semantic')}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <TrendingUp className="w-6 h-6 text-red-600" />
                      <h4 className="font-semibold text-gray-900">{examples.semantic.title}</h4>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <p className="text-sm text-gray-600 mb-2">Example:</p>
                      <div className="text-xs font-mono bg-gray-50 p-2 rounded">{examples.semantic.example}</div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!definitionType}
                    className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 disabled:opacity-50 transition-all"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Formula Builder */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Build the formula
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Select components and create the calculation.
                  </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left: Component Selection */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      Select {definitionType === 'kpi' ? 'KPIs' : 'Semantics'} ({selectedItems.length})
                    </h4>

                    <div className="relative mb-3">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      />
                    </div>

                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      {filteredItems.map(item => (
                        <label key={item} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item)}
                            onChange={() => handleItemToggle(item)}
                            className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                          />
                          <span className="ml-3 text-sm text-gray-700">{item}</span>
                        </label>
                      ))}
                    </div>

                    {selectedItems.length > 0 && (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                          {selectedItems.map(item => (
                            <span
                              key={item}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                definitionType === 'kpi' 
                                  ? 'bg-orange-100 text-orange-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item}
                              <button
                                onClick={() => handleItemToggle(item)}
                                className="hover:bg-white hover:bg-opacity-30 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Formula */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Formula</h4>
                      <button
                        onClick={() => setEquation(generateEquationSuggestion())}
                        disabled={selectedItems.length === 0}
                        className="text-sm text-orange-600 hover:text-orange-700 disabled:text-gray-400"
                      >
                        <Zap className="w-4 h-4 inline mr-1" />
                        Auto-generate
                      </button>
                    </div>

                    <textarea
                      value={equation}
                      onChange={(e) => setEquation(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono text-sm"
                      placeholder="e.g., (Revenue + Customer_Satisfaction) / 2"
                    />

                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Unit Type</label>
                      <select
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      >
                        <option value="number">Number</option>
                        <option value="currency">Currency ($)</option>
                        <option value="percentage">Percentage (%)</option>
                        <option value="ratio">Ratio</option>
                      </select>
                    </div>

                    {equation && selectedItems.length > 0 && (
                      <div className="mt-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3">
                        <h5 className="font-medium text-gray-900 mb-2">Preview</h5>
                        <div className="text-sm space-y-1">
                          <div><strong>{termName}</strong> = {equation}</div>
                          <div>Unit: {unit}</div>
                          <div>Components: {selectedItems.length}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!equation.trim() || selectedItems.length === 0}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all"
                  >
                    <Save className="w-4 h-4" />
                    Save Definition
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Settings Panel with Orange/Red theme
  const SettingsPanel = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('semantics');
    
    const existingDefinitions = [
      { term: 'efficiency', formula: '(Revenue / Operating_Costs) * 100', type: 'kpi', status: 'active' },
      { term: 'customer satisfaction', formula: '(Customer_Score + Quality_Index) / 2', type: 'kpi', status: 'active' },
      { term: 'growth potential', formula: 'revenue_growth * 0.7 + efficiency * 0.3', type: 'semantic', status: 'draft' }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-orange-50 to-red-50">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-orange-600" />
              <h2 className="text-xl font-semibold text-gray-900">Settings</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex">
            {/* Sidebar */}
            <div className="w-48 border-r border-gray-200 p-4">
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab('semantics')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                    activeTab === 'semantics' 
                      ? 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Semantic Definitions
                </button>
                <button
                  onClick={() => setActiveTab('general')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                    activeTab === 'general' 
                      ? 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  General Settings
                </button>
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-y-auto max-h-96">
              {activeTab === 'semantics' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Your Semantic Definitions</h3>
                    <button
                      onClick={() => {
                        setSemanticBuilderData({ unknownTerm: '' });
                        setShowSemanticBuilder(true);
                      }}
                      className="px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg text-sm hover:from-orange-600 hover:to-red-600 transition-all"
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      New Definition
                    </button>
                  </div>

                  <div className="space-y-3">
                    {existingDefinitions.map((def, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">{def.term}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                def.type === 'kpi' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {def.type}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                def.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {def.status}
                              </span>
                            </div>
                            <div className="text-sm font-mono text-gray-600 bg-gray-50 p-2 rounded">
                              {def.formula}
                            </div>
                          </div>
                          <button className="text-gray-400 hover:text-orange-600 ml-2">
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'general' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Time Period
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option>Current Quarter</option>
                        <option>Current Year</option>
                        <option>Last 12 Months</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Currency Format
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                        <option>USD ($)</option>
                        <option>EUR (€)</option>
                        <option>GBP (£)</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mock data generators for demonstrations
  const generateMockAnalysis = (query) => {
    const lowerQuery = query.toLowerCase();
    
    // Check for specific KPI questions
    if (lowerQuery.includes('accessory sales') && lowerQuery.includes('april')) {
      return "Accessory Sales Revenue - NCS (PDI) Analysis\n\nPerformance Summary:\nActual performance of $65,202 against target of $212,000 represents a significant 69.2% shortfall for April. This underperformance indicates critical challenges in the pre-delivery inspection accessory sales process.\n\nRoot Cause:\nPrimary driver is lower confirmed orders during the PDI phase, suggesting conversion issues in customer engagement or inventory availability.\n\nRecommended Actions:\n• Conduct comprehensive dealer feedback survey to identify PDI upselling barriers\n• Launch limited-time accessory bundle promotions to drive immediate sales\n• Implement enhanced dealer training for improved conversion during vehicle delivery\n• Review PDI process timing to optimize customer touchpoints\n\nThis revenue gap requires immediate attention to prevent Q2 deterioration.";
    }
    
    if (lowerQuery.includes('workshop parts') && lowerQuery.includes('july')) {
      return "Workshop Parts Sales Performance Analysis\n\nPerformance Summary:\nJuly workshop parts sales reached $5.76M against $7.61M target, showing 24.3% underperformance. This represents $1.85M in missed revenue opportunity.\n\nRoot Cause:\nSupply chain delays in parts arrival directly impacted service capacity and sales performance, indicating vulnerability in inventory management.\n\nRecommended Actions:\n• Implement advanced inventory forecasting to anticipate demand patterns\n• Establish priority handling for high-frequency workshop parts\n• Strengthen logistics partnerships to improve delivery reliability\n• Expand supplier base to reduce dependency risks\n• Deploy real-time inventory tracking for early shortage warnings\n\nAddressing these supply chain challenges is essential for Q3 target achievement.";
    }
    
    // Default analyses for other queries
    const analyses = [
      "Based on your Q1 2024 data, performance metrics show strong upward momentum with 18.5% revenue growth compared to Q1 2023. Efficiency indicators are performing optimally at 87%, exceeding the target benchmark of 85%. Customer satisfaction has improved significantly by 12 points, reaching 8.4 out of 10.\n\nKey operational highlights include a 15% reduction in customer acquisition costs while maintaining quality service standards. The sales pipeline remains robust with $2.3M in qualified opportunities. Regional performance shows North America leading with 22% growth, while EMEA demonstrates steady 8% growth. These trends indicate strong positioning for achieving annual targets.",
      
      "Current efficiency analysis reveals mixed performance indicators across business units. Overall productivity has increased 11% year-over-year, though some operational bottlenecks are emerging in supply chain operations. Customer retention rates have improved to 94%, up from 91% last quarter.\n\nFinancial performance shows healthy margins at 23.5%, with close monitoring of increased raw material costs. Digital transformation investments are yielding positive returns with 28% improvement in process automation efficiency. Market share has grown by 1.2 percentage points, demonstrating competitive strength in core market segments.",
      
      "Strategic performance metrics indicate successful business model evolution and diversification. Revenue diversification efforts have reduced single-product dependency from 65% to 48%, creating more resilient income streams. Subscription services introduction generated $1.8M in recurring revenue.\n\nOperational excellence initiatives improved employee productivity by 16% while maintaining engagement scores of 4.2/5. Customer lifetime value increased 31% through enhanced product offerings and retention strategies. These foundational improvements support sustained long-term growth objectives."
    ];
    return analyses[Math.floor(Math.random() * analyses.length)];
  };

  const generateMockChartData = (query = '') => {
    const lowerQuery = query.toLowerCase();
    
    // Specific chart data for accessory sales
    if (lowerQuery.includes('accessory sales') && lowerQuery.includes('april')) {
      return {
        type: 'circular',
        title: 'Accessory Sales Revenue Performance - April',
        metrics: [
          { 
            label: 'Achievement Rate', 
            value: 65202, 
            target: 212000
          },
          { 
            label: 'Revenue Gap', 
            value: 146798, 
            target: 212000
          },
          { 
            label: 'Target Progress', 
            value: 30.8, 
            target: 100
          }
        ]
      };
    }
    
    // Specific chart data for workshop parts sales
    if (lowerQuery.includes('workshop parts') && lowerQuery.includes('july')) {
      return {
        type: 'bar',
        title: 'Workshop Parts Sales - July Performance',
        data: [
          { label: 'Actual Sales', value: 5756065, formatted: '$5.76M' },
          { label: 'Target Sales', value: 7605633, formatted: '$7.61M' },
          { label: 'Revenue Gap', value: 1849568, formatted: '$1.85M' }
        ]
      };
    }
    
    // Default chart types for other queries
    const chartTypes = ['bar', 'circular'];
    const chartType = chartTypes[Math.floor(Math.random() * chartTypes.length)];
    
    if (chartType === 'bar') {
      return {
        type: 'bar',
        title: 'Performance by Business Unit',
        data: [
          { label: 'Sales', value: 125000, formatted: '$125K' },
          { label: 'Marketing', value: 89000, formatted: '$89K' },
          { label: 'Operations', value: 156000, formatted: '$156K' },
          { label: 'Customer Success', value: 98000, formatted: '$98K' }
        ]
      };
    } else {
      return {
        type: 'circular',
        title: 'Quarterly Performance Metrics',
        metrics: [
          { label: 'Revenue Growth', value: 125000, target: 100000 },
          { label: 'Efficiency Score', value: 87, target: 85 },
          { label: 'Customer Satisfaction', value: 8.4, target: 8.0 }
        ]
      };
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (type, content, data = null) => {
    const newMessage = {
      id: Date.now() + Math.random(),
      type,
      content,
      data,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  const handleSendMessage = async (message = currentInput) => {
    if (!message.trim() || loading) return;

    addMessage('user', message);
    setCurrentInput('');
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Mock validation logic
      const validQuestionPatterns = [
        /show.*revenue/i, /analyze.*performance/i, /what.*efficiency/i, 
        /display.*kpi/i, /revenue.*growth/i, /customer.*satisfaction/i,
        /sales.*volume/i, /profit.*margin/i, /operating.*costs/i,
        /accessory.*sales/i, /workshop.*parts/i
      ];
      
      const isValidQuestion = validQuestionPatterns.some(pattern => pattern.test(message));
      
      // Check for unknown terms
      const unknownTerms = ['performance', 'efficiency', 'operational excellence', 'customer value'];
      const foundUnknownTerm = unknownTerms.find(term => 
        message.toLowerCase().includes(term.toLowerCase()) &&
        !demoAvailableOptions.semantics.includes(term)
      );

      if (foundUnknownTerm) {
        addMessage('assistant', `I found the term "${foundUnknownTerm}" in your question, but I don't have a definition for it yet. Would you like me to help you define it so I can provide better analysis?`, {
          type: 'unknown_term',
          term: foundUnknownTerm
        });
      } else if (isValidQuestion) {
        // Check if this is one of our specific KPI questions
        const isSpecificKPI = (message.toLowerCase().includes('accessory sales') && message.toLowerCase().includes('april')) ||
                             (message.toLowerCase().includes('workshop parts') && message.toLowerCase().includes('july'));
        
        if (isSpecificKPI) {
          // Generate analysis directly for specific KPIs
          const mockAnalysis = generateMockAnalysis(message);
          const mockChart = generateMockChartData(message);
          
          addMessage('assistant', '', {
            type: 'analysis_result',
            gpt_analysis: mockAnalysis,
            chart_data: mockChart,
            query: message
          });
          
          // Focus input after analysis for next question
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 500);
        } else {
          // Simulate missing entity detection for other questions
          const missingEntities = [];
          
          // Check if question mentions specific entities
          const hasTimeReference = /q1|q2|q3|q4|january|february|march|2024|2023/i.test(message);
          const hasDistributorReference = /acme|beta|central|delta/i.test(message);
          
          if (!hasTimeReference && Math.random() > 0.3) {
            missingEntities.push('period');
          }
          if (!hasDistributorReference && Math.random() > 0.4) {
            missingEntities.push('distributor');
          }
          if (Math.random() > 0.6) {
            missingEntities.push('fnyear');
          }

          if (missingEntities.length > 0) {
            // Show ONE entity selection at a time
            const firstMissingEntity = missingEntities[0];
            addMessage('assistant', `I understand your question about business metrics. To provide you with accurate analysis, I need to know the ${fieldConfig[firstMissingEntity]?.label?.toLowerCase() || firstMissingEntity}:`, {
              type: 'single_entity_selection',
              missing_entity: firstMissingEntity,
              original_question: message,
              all_missing: missingEntities
            });
            
            // Set entity selection state FIRST
            const entityState = {
              originalQuestion: message,
              allMissingEntities: missingEntities,
              currentEntity: firstMissingEntity,
              collectedEntities: {},
              remainingEntities: missingEntities.slice(1)
            };
            setWaitingForEntitySelection(entityState);
            
            // Then immediately set autocomplete options
            setTimeout(() => {
              const expectedType = firstMissingEntity;
              const entityOptions = demoAvailableOptions[expectedType === 'fnyear' ? 'fnyear' : expectedType + 's'] || demoAvailableOptions[expectedType] || [];
              const defaultOptions = ['All'];
              const topOptions = entityOptions.filter(opt => opt.toLowerCase() !== 'all').slice(0, 4);
              const options = [...defaultOptions, ...topOptions].map(option => ({ 
                type: expectedType, 
                value: option, 
                color: getEntityColor(expectedType),
                isEntityResponse: true
              }));
              
              setAutocompleteOptions(options);
              setSelectedSuggestionIndex(-1);
              
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }, 200);
            
          } else {
            // Generate analysis directly
            const mockAnalysis = generateMockAnalysis(message);
            const mockChart = generateMockChartData(message);
            
            addMessage('assistant', '', {
              type: 'analysis_result',
              gpt_analysis: mockAnalysis,
              chart_data: mockChart,
              query: message
            });
            
            // Focus input after analysis for next question
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }, 500);
          }
        }
      } else {
        addMessage('assistant', `I can help you analyze your business metrics and KPIs. Try asking about revenue, performance, efficiency, or other business metrics. For example: "Show me accessory sales revenue performance for April" or "Analyze workshop parts sales for July".`);
        
        // Focus input after response
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      }

    } catch (error) {
      addMessage('assistant', "Sorry, I encountered an error while analyzing your request. Please try again.");
      
      // Focus input after error
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleEntitySelection = (field, value) => {
    if (!waitingForEntitySelection) return;
    
    const updatedCollectedEntities = {
      ...waitingForEntitySelection.collectedEntities,
      [field]: value
    };
    
    // Check if there are more entities to collect
    if (waitingForEntitySelection.remainingEntities.length > 0) {
      // Ask for next entity
      const nextEntity = waitingForEntitySelection.remainingEntities[0];
      addMessage('assistant', `Great! You selected ${value}. Now I need the ${fieldConfig[nextEntity]?.label?.toLowerCase() || nextEntity}:`, {
        type: 'single_entity_selection',
        missing_entity: nextEntity,
        original_question: waitingForEntitySelection.originalQuestion,
        collected_entities: updatedCollectedEntities
      });
      
      setWaitingForEntitySelection({
        ...waitingForEntitySelection,
        collectedEntities: updatedCollectedEntities,
        currentEntity: nextEntity,
        remainingEntities: waitingForEntitySelection.remainingEntities.slice(1)
      });
      
      // Focus input for next entity question
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      // All entities collected, process the question
      setWaitingForEntitySelection(null);
      addMessage('assistant', `Perfect! Now I have all the information I need. Let me analyze ${waitingForEntitySelection.originalQuestion.toLowerCase()} for you...`);
      
      setTimeout(() => {
        const mockAnalysis = generateMockAnalysis(waitingForEntitySelection.originalQuestion);
        const mockChart = generateMockChartData(waitingForEntitySelection.originalQuestion);
        
        addMessage('assistant', '', {
          type: 'analysis_result',
          gpt_analysis: mockAnalysis,
          chart_data: mockChart,
          query: waitingForEntitySelection.originalQuestion,
          entities: updatedCollectedEntities
        });
        
        // Focus input for next question
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 500);
      }, 1000);
    }
  };

  const handleEntitySkip = (field) => {
    if (!waitingForEntitySelection) return;
    
    if (waitingForEntitySelection.remainingEntities.length > 0) {
      const nextEntity = waitingForEntitySelection.remainingEntities[0];
      addMessage('assistant', `Okay, I'll skip that. Let me ask about the ${fieldConfig[nextEntity]?.label?.toLowerCase() || nextEntity}:`, {
        type: 'single_entity_selection',
        missing_entity: nextEntity,
        original_question: waitingForEntitySelection.originalQuestion,
        collected_entities: waitingForEntitySelection.collectedEntities
      });
      
      setWaitingForEntitySelection({
        ...waitingForEntitySelection,
        currentEntity: nextEntity,
        remainingEntities: waitingForEntitySelection.remainingEntities.slice(1)
      });
    } else {
      setWaitingForEntitySelection(null);
      addMessage('assistant', `No problem! Let me analyze what I can with the information available...`);
      
      setTimeout(() => {
        const mockAnalysis = generateMockAnalysis(waitingForEntitySelection.originalQuestion);
        const mockChart = generateMockChartData(waitingForEntitySelection.originalQuestion);
        
        addMessage('assistant', '', {
          type: 'analysis_result',
          gpt_analysis: mockAnalysis,
          chart_data: mockChart,
          query: waitingForEntitySelection.originalQuestion
        });
      }, 1000);
    }
  };

  const handleCreateSemanticDefinition = (term) => {
    setSemanticBuilderData({ unknownTerm: term });
    setShowSemanticBuilder(true);
  };

  const handleSaveSemanticDefinition = async (definition) => {
    try {
      console.log('Saving semantic definition:', definition);
      
      if (!demoAvailableOptions.semantics.includes(definition.term)) {
        demoAvailableOptions.semantics.push(definition.term);
      }
      
      addMessage('assistant', `Excellent! I've learned that "${definition.term}" means: ${definition.formula}. Now I can use this in my analysis. Let me process your original question with this new understanding...`, {
        type: 'definition_saved',
        term: definition.term
      });
      
      setTimeout(() => {
        const mockAnalysis = generateMockAnalysis(definition.term);
        const mockChart = generateMockChartData(definition.term);
        
        addMessage('assistant', '', {
          type: 'analysis_result',
          gpt_analysis: mockAnalysis,
          chart_data: mockChart,
          query: `Analysis of ${definition.term}`
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error saving definition:', error);
      addMessage('assistant', 'Sorry, I had trouble saving that definition. Please try again.');
    }
  };

  const renderMessage = (message) => {
    if (message.type === 'user') {
      return (
        <div key={message.id} className="flex justify-end mb-6">
          <div className="flex items-start gap-3 max-w-4xl">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-2xl rounded-br-md px-6 py-4 shadow-sm">
              <p className="text-sm leading-relaxed">{message.content}</p>
            </div>
            <div className="w-8 h-8 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-orange-600" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={message.id} className="flex justify-start mb-6">
        <div className="flex items-start gap-3 max-w-4xl">
          <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm flex-1">
            {message.data?.type === 'analysis_result' ? (
              <div className="p-6">
                {/* Key Metrics Summary for specific KPIs */}
                {(message.data.query.toLowerCase().includes('accessory sales') || message.data.query.toLowerCase().includes('workshop parts')) && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Metrics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {message.data.query.toLowerCase().includes('accessory sales') && (
                        <>
                          <div className="text-center">
                            <div className="text-gray-600">Actual</div>
                            <div className="font-semibold text-gray-900">$65,202</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Target</div>
                            <div className="font-semibold text-gray-900">$212,000</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Deviation</div>
                            <div className="font-semibold text-red-600">-69.2%</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Month</div>
                            <div className="font-semibold text-gray-900">April</div>
                          </div>
                        </>
                      )}
                      {message.data.query.toLowerCase().includes('workshop parts') && (
                        <>
                          <div className="text-center">
                            <div className="text-gray-600">Actual</div>
                            <div className="font-semibold text-gray-900">$5.76M</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Target</div>
                            <div className="font-semibold text-gray-900">$7.61M</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Deviation</div>
                            <div className="font-semibold text-red-600">-24.3%</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-600">Month</div>
                            <div className="font-semibold text-gray-900">July</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Main Analysis Text */}
                <div className="prose prose-gray max-w-none mb-6">
                  <div className="text-gray-800 leading-relaxed whitespace-pre-line text-base">
                    {message.data.gpt_analysis}
                  </div>
                </div>

                {/* Show collected entities if available */}
                {message.data.entities && (
                  <div className="mb-4 p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                    <p className="text-sm font-medium text-orange-800 mb-2">Analysis Parameters:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(message.data.entities).map(([key, value]) => (
                        <span key={key} className="inline-flex items-center px-2 py-1 bg-white rounded text-xs">
                          <span className="text-gray-600">{fieldConfig[key]?.label || key}:</span>
                          <span className="ml-1 font-medium text-gray-900">{value}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chart Visualization */}
                <div className="chart-container">
                  {message.data.chart_data?.type === 'bar' ? (
                    <SimpleBarChart 
                      data={message.data.chart_data.data} 
                      title={message.data.chart_data.title}
                    />
                  ) : message.data.chart_data?.type === 'circular' ? (
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800 mb-6 text-center">
                        {message.data.chart_data.title}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {message.data.chart_data.metrics.map((metric, index) => (
                          <CircularProgress
                            key={index}
                            value={metric.value}
                            target={metric.target}
                            label={metric.label}
                            size={120}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : message.data?.type === 'single_entity_selection' ? (
              <div className="px-6 py-4">
                <p className="text-gray-800 leading-relaxed">{message.content}</p>
                
                {/* Show collected entities if any */}
                {message.data.collected_entities && Object.keys(message.data.collected_entities).length > 0 && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg">
                    <p className="text-xs font-medium text-orange-800 mb-2">Already selected:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(message.data.collected_entities).map(([key, value]) => (
                        <span key={key} className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                          {fieldConfig[key]?.label || key}: {value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            ) : message.data?.type === 'unknown_term' ? (
              <div className="px-6 py-4">
                <p className="text-gray-800 leading-relaxed mb-4">{message.content}</p>
                
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="text-orange-800 font-medium">Unknown Term: "{message.data.term}"</p>
                      <p className="text-orange-700 text-sm mt-1">
                        Define this term so I can provide more accurate analysis in the future.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => handleCreateSemanticDefinition(message.data.term)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg text-sm hover:from-orange-600 hover:to-red-600 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Define "{message.data.term}"
                  </button>
                  <button
                    onClick={() => addMessage('assistant', `Here are some common interpretations of "${message.data.term}" in business analysis...`)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                  >
                    Show Examples
                  </button>
                </div>
              </div>
            ) : message.data?.type === 'definition_saved' ? (
              <div className="px-6 py-4">
                <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-green-800 font-medium">Definition Saved!</p>
                      <p className="text-green-700 text-sm mt-1">{message.content}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-6 py-4">
                <p className="text-gray-800 leading-relaxed">{message.content}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const clearChat = () => {
    setMessages([{
      id: 1,
      type: 'assistant',
      content: "Hello! I'm your business intelligence assistant. I can analyze your KPIs, help you understand performance metrics, and learn new definitions when needed. What would you like to know about your business?",
      timestamp: new Date()
    }]);
    setCurrentInput('');
    setLoading(false);
    setWaitingForEntitySelection(null);
    setAutocompleteOptions([]);
    setSelectedSuggestionIndex(-1);
    setKeyNavigationUsed(false);
  };

  const suggestedQuestions = [
    "Show me accessory sales revenue performance for April",
    "Analyze workshop parts sales for July", 
    "What's our operational efficiency?",
    "Display sales volume by distributor"
  ];

  return (
    <div className="max-w-6xl mx-auto h-screen flex flex-col" style={{ backgroundColor: '#fafafa' }}>
      {/* Header with Orange/Red branding */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">AutoSmart AI</h1>
              <p className="text-sm text-gray-600">Your growth assistant ask about your company</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-orange-600 border border-gray-300 rounded-lg hover:border-orange-300 transition-all"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            
            {messages.length > 1 && (
              <button
                onClick={clearChat}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                New Analysis
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages Container - ChatGPT style */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {messages.map(renderMessage)}
          
          {loading && (
            <div className="flex justify-start mb-6">
              <div className="flex items-start gap-3 max-w-4xl">
                <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-6 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <Loader className="w-5 h-5 animate-spin text-orange-600" />
                    <span className="text-gray-600">Analyzing your data and generating insights...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Enhanced Input Area with Smart Autocomplete */}
      <div className="bg-white border-t border-gray-200 px-6 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">Get started with these business questions:</p>
              <div className="grid grid-cols-2 gap-3">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentInput(question)}
                    disabled={loading}
                    className="p-3 text-left bg-gradient-to-r from-orange-50 to-red-50 hover:from-orange-100 hover:to-red-100 rounded-lg text-sm text-gray-700 transition-all disabled:opacity-50 border border-orange-200"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Smart Input with Compact Inline Autocomplete */}
          <div className="relative">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={currentInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    waitingForEntitySelection 
                      ? `Type your ${fieldConfig[waitingForEntitySelection.currentEntity]?.label?.toLowerCase() || waitingForEntitySelection.currentEntity} or select from options below...`
                      : "Ask me about your business metrics... (try typing 'revenue', 'Q1', or distributor names)"
                  }
                  className="w-full px-6 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base placeholder-gray-500"
                  disabled={loading}
                />
                
                {/* Compact Inline Suggestions - appear right in the text field */}
                {(autocompleteOptions.length > 0 || waitingForEntitySelection) && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50">
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {waitingForEntitySelection ? 'Answer options:' : 'Suggestions:'}
                        </span>
                        <div className="flex gap-2 flex-1 overflow-x-auto">
                          {autocompleteOptions.map((option, index) => (
                            <button
                              key={index}
                              onClick={() => handleSmartAutocompleteSelect(option)}
                              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ease-out ${
                                selectedSuggestionIndex === index
                                  ? `bg-orange-200 text-orange-800 shadow-md`
                                  : option.isEntityResponse 
                                    ? `${option.color} shadow-sm hover:shadow-md hover:scale-101 ${!keyNavigationUsed ? 'animate-pulse' : ''}` 
                                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 hover:shadow-sm hover:scale-101'
                              }`}
                            >
                              <span className="font-medium">{option.value}</span>
                              {!option.isEntityResponse && (
                                <span className={`ml-1 px-1 rounded text-xs ${option.color}`}>
                                  {option.type}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {waitingForEntitySelection ? '←→ Navigate • Enter to select' : '←→ Navigate • Tab to use'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Entity Recognition Indicator */}
                {currentInput && (
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <div className="flex items-center gap-1">
                      {allAvailableTerms.some(term => 
                        currentInput.toLowerCase().includes(term.value.toLowerCase())
                      ) && (
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Recognized terms found"></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => handleSendMessage()}
                disabled={loading || !currentInput.trim()}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all font-medium shadow-lg"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-500">
              {waitingForEntitySelection 
                ? `Waiting for ${fieldConfig[waitingForEntitySelection.currentEntity]?.label?.toLowerCase() || waitingForEntitySelection.currentEntity} selection`
                : 'AI-powered business intelligence with smart suggestions'
              }
            </p>
            
            {/* Debug info */}
            {waitingForEntitySelection && (
              <div className="text-xs text-orange-600">
                DEBUG: Entity={waitingForEntitySelection.currentEntity} Options={autocompleteOptions.length} Selected={selectedSuggestionIndex} KeyUsed={keyNavigationUsed}
              </div>
            )}
            
            {/* Compact Entity Recognition Status */}
            {currentInput && autocompleteOptions.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  autocompleteOptions[0]?.isEntityResponse ? 'bg-orange-500' : 'bg-violet-500'
                }`}></div>
                <span>
                  {autocompleteOptions[0]?.isEntityResponse ? 'Entity options available' : 'Smart suggestions active'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Semantic Definition Builder Modal */}
      {showSemanticBuilder && (
        <SemanticDefinitionBuilder
          onClose={() => {
            setShowSemanticBuilder(false);
            setSemanticBuilderData(null);
          }}
          onSave={handleSaveSemanticDefinition}
          unknownTerm={semanticBuilderData?.unknownTerm || ''}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <SettingsPanel
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default OrangeRedKPIInterface;